<template>
  <div v-if="data">
    <div v-if="t['w.saal.toode.banner']()" v-html="t['w.saal.toode.banner']()" />

    <h1 class="my-10 text-center">{{ t['param.search.title']() }}: {{ searchTerm }}</h1>

    <div ref="top" class="relative">
      <FilterBar
        ref="filterBar"
        :filters="data.filters"
        :applied-filters="filters"
        :order-by-options="orderByOptions"
        :default-sort-order="defaultSortOrder"
        :order-by="effectiveOrderBy"
        @order-by-changed="setOrderBy"
        @update-choices="(group: number, choices: number[]) => updateChoices(group, choices)"
        @update-range="(group: number, min: number, max: number) => updateRange(group, min, max)"
      />
      <FilterMenu
        :filters="data.filters"
        :applied-filters="filters"
        :order-by-options="orderByOptions"
        :default-sort-order="defaultSortOrder"
        :order-by="effectiveOrderBy"
        @order-by-changed="setOrderBy"
        @filter-clear="clearFilters"
        @update-choices="(group: number, choices: number[]) => updateChoices(group, choices)"
        @update-range="(group: number, min: number, max: number) => updateRange(group, min, max)"
      />
      <AppliedFiltersList :filters="data.filters" :applied-filters="filters" @filter-remove="removeFilter" @filter-clear="clearFilters" />

      <div v-if="data.products.length" class="h-container overflow-hidden">
        <ProductGrid :products="data.products" />
      </div>

      <div v-if="!data.products.length" class="h-container">
        <NoResults class="my-10" :title="noResultsData.title" :suggestions="noResultsData.suggestions" :bottom-text="noResultsData.bottomText" />
      </div>

      <Pagination v-if="data.products.length" param="slk" :current-page="currentPage" :pages="data.pages" :scroll-to-top-element="elementTop" />
    </div>

    <LastViewedProductsSection class="h-container flex flex-col items-center py-10" />
  </div>
</template>

<script setup lang="ts">
import FilterBar from '~/components/filter-bar/FilterBar.vue'
import FilterMenu from '~/components/filter-bar/FilterMenu.vue'
import AppliedFiltersList from '~/components/hall/filter/AppliedFiltersList.vue'
import Pagination from '~/components/pagination/Pagination.vue'
import NoResults from '~/components/no-results/NoResults.vue'

import LastViewedProductsSection from '~/components/last-viewed-products-section/LastViewedProductsSection.vue'
import * as t from '~/messages'
import { useSearchQuery } from '~/composables/queries/search'

const orderByOptions: Record<string, string> = {
  '0': t['w.saal.reasta.populaarsus'](),
  '1': t['w.saal.reasta.odavamast'](),
  '2': t['w.saal.reasta.kallimast'](),
  // '3': t['w.saal.reasta.soodus'](),
  '4': t['w.saal.reasta.uudsus'](),
  '8': t['w.saal.reasta.tarne'](),
}

const {
  public: { countryId },
} = useRuntimeConfig()

const route = useRoute()
const elementTop = useTemplateRef('top')
const filterBar = useTemplateRef('filterBar')

const defaultSortOrder = '0'

const { filters, filterParameters, currentPage, setOrderBy, effectiveOrderBy, removeFilter, clearFilters, updateChoices, updateRange } = useHallRefinements({
  sortOrderParameter: 'so',
  defaultSortOrder,
  orderByOptions,
  pageParameter: 'slk',
  filterBar,
  scrollToTopElement: elementTop,
})

const searchTerm = computed(() => (typeof route.query.q === 'string' ? route.query.q : ''))

const { data, error, suspense } = await useSearchQuery(searchTerm, currentPage, effectiveOrderBy, filterParameters)
await suspense()
if (error.value) {
  throw createError(error.value)
}

const noResultsData = useNoSearchResults()

const orderByCheapest = t['w.saal.reasta.odavamast']()
const orderByMostExpensive = t['w.saal.reasta.kallimast']()
const metaPageTitle = t['meta.title.page']()
const searchPageTitle = t['param.search.title']()

useHead({
  title: () => {
    let title = searchPageTitle

    if (effectiveOrderBy.value === '1') {
      title += ` - ${orderByCheapest}`
    }

    if (effectiveOrderBy.value === '2') {
      title += ` - ${orderByMostExpensive}`
    }

    if (currentPage.value > 1) {
      title += ` - ${metaPageTitle}${currentPage.value}`
    }

    return title
  },
  link: () => [data.value?.relPrev ? { rel: 'prev', href: data.value.relPrev } : undefined, data.value?.relNext ? { rel: 'next', href: data.value.relNext } : undefined],
  meta: [{ name: 'robots', content: 'noindex, follow' }],
})

const user = useUserState()

const { productImpressions } = useCriteo()
const criteoProductsListEvent = () => {
  if (countryId !== 2) {
    return
  }

  const email = user.value ? user.value.emailMd5 : null
  const products = data.value.products.map((product) => product.productId)

  productImpressions(email, products)
}

criteoProductsListEvent()
</script>
